/**
 * Coup de pouce — Attendu 1049 « faire vivre la notion de nombre quotient »
 * (automatisme 5e n°11). UNE page STATIQUE partagée par les quatre
 * generator_keys (cadrage prof 2026-09-07).
 *
 * L'élève bloqué sur 3 × … = 7 n'a pas encore fait le lien entre la barre
 * de fraction et le signe ÷ : une division ne l'aide pas. Ce qu'il a, c'est
 * une image de PARTAGE, celle par laquelle la 6e a introduit le sens
 * quotient (coup de pouce 621 : la bande coupée en 4). C'est le schéma en
 * barres de 1046, le même dessin, une lecture différente :
 *   - en haut, l'égalité 4 × ? = 13 ;
 *   - une barre entière, étiquetée 13 ;
 *   - dessous, la même longueur coupée en 4 parts égales, chacun
 *     étiqueté 13/4 ;
 *   - sous le schéma, en gros : ? = 13/4 ;
 *   - deux lignes : « 4 parts égales qui font 13 : une part, ça
 *     s'écrit 13/4. Je ne calcule rien, je l'écris. » et « Je vérifie :
 *     4 parts de 13/4, ça fait bien 13. » (le geste 2 des cartes).
 * La seule chose que la figure enseigne : lire « 4 × ? = 13 » comme
 * « 4 parts égales font 13 ». Le tout est en haut de la fraction, le
 * nombre de parts en bas : l'inversion 4/13 ne se lit pas sur le dessin.
 * Rien à compter, rien à couper en quarts, pas de division, pas de lettres.
 *
 * COUPLE SACRIFIÉ : (4, 13) ne sort d'aucune carte (gen_1049, auto-testé
 * par cdp1049.self.test.js).
 *
 * Le texte vit DANS la figure, largeur bornée à 340 px et tailles de 1048 (la
 * fiche affiche les lignes avant la figure, or ici c'est le schéma qui
 * explique et le texte qui conclut). Les phrases sont reprises dans
 * l'aria-label, que la fiche lit pour la synthèse vocale.
 */

const ORANGE = '#EA580C';
const ORANGE_CLAIR = '#FED7AA';
const BLEU = '#2563EB';
const VERT = '#15803D';
const GRIS = '#E2E8F0';
const ENCRE = '#1E293B';

/** La barre : 320 px pour 13, 4 parts de 80. */
const XB = 40;
const WB = 320;
const NB = 4;
const WM = WB / NB;

/** Une fraction empilée, centrée en (x, y = la barre), une seule couleur. */
const frac = (x, y, n, d, couleur, taille, demi) => `
  <text x="${x}" y="${y - 4}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${n}</text>
  <line x1="${x - demi}" y1="${y}" x2="${x + demi}" y2="${y}" stroke="${couleur}" stroke-width="1.8"/>
  <text x="${x}" y="${y + taille + 1}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${d}</text>`;

/** Les 4 parts égales, chacune étiquetée 13/4. */
const PARTS = Array.from({ length: NB }, (_, k) => {
    const x = XB + k * WM;
    return `<rect x="${x}" y="100" width="${WM}" height="36" fill="${ORANGE_CLAIR}" stroke="${ORANGE}" stroke-width="2"/>
  ${frac(x + WM / 2, 118, 13, 4, ORANGE, 10, 8)}`;
}).join('\n  ');

const SVG_BARRES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 318" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="Schéma en barres : 4 fois quel nombre égale 13 ? Une barre entière de 13, et dessous la même longueur coupée en 4 parts égales, chacune étiquetée 13 sur 4. Le nombre cherché, c'est 13 sur 4. 4 parts égales qui font 13 : une part, ça s'écrit 13 sur 4. Je ne calcule rien, je l'écris. Je vérifie : 4 parts de 13 sur 4, ça fait bien 13.">
  <text x="200" y="30" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${BLEU}">4</tspan> × <tspan fill="${ORANGE}">?</tspan> = <tspan fill="${VERT}">13</tspan></text>
  <rect x="${XB}" y="52" width="${WB}" height="36" fill="${GRIS}" stroke="${ENCRE}" stroke-width="1.5"/>
  <text x="200" y="77" font-size="15" font-weight="bold" text-anchor="middle" fill="${VERT}">13</text>
  ${PARTS}
  <text x="192" y="183" font-size="17" font-weight="bold" text-anchor="end" fill="${ENCRE}"><tspan fill="${ORANGE}">?</tspan> =</text>
  ${frac(214, 176, 13, 4, ORANGE, 15, 12)}
  <text x="200" y="228" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${BLEU}">4 parts égales</tspan> qui font <tspan font-weight="bold" fill="${VERT}">13</tspan> :</text>
  <text x="236" y="250" font-size="12" text-anchor="end" fill="${ENCRE}">une part, ça s'écrit</text>
  ${frac(252, 245, 13, 4, ORANGE, 10, 7)}
  <text x="264" y="250" font-size="12" fill="${ENCRE}">.</text>
  <text x="200" y="273" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">Je ne calcule rien, je l'écris.</text>
  <text x="224" y="304" font-size="12" text-anchor="end" fill="${ENCRE}">Je vérifie : <tspan font-weight="bold" fill="${BLEU}">4</tspan> parts de</text>
  ${frac(240, 299, 13, 4, ORANGE, 10, 7)}
  <text x="254" y="304" font-size="12" fill="${ENCRE}">, ça fait bien <tspan font-weight="bold" fill="${VERT}">13</tspan>.</text>
</svg>`.trim();

const AIDE = {
    pages: [
        { titre: '4 parts égales qui font 13', svg: SVG_BARRES },
    ],
};

const CP_1049 = {
    flash_texte_trou_saisie: AIDE,
    sens_qcm_unique: AIDE,
    sens_tableau_vf: AIDE,
    sens_texte_trou_drag: AIDE,
};

export default CP_1049;
